/**
 * CollectionGroupAddPage — 묶음에 카드 수동 추가 (IA SC-016).
 *
 * 이름·번호로 검색해 고른 카드를 묶음에 넣는다.
 *
 * ⚠ **수동 추가는 보유가 되지 않는다** (IA SC-016). 여기서 하는 일은 분류뿐이고, 보유는
 *   스캔 결과나 카드 상세의 '담기' 로만 생긴다. 그래서 넣은 카드가 묶음 상세에서
 *   '미보유' 로 보이는 것이 정상이다 — 화면도 그렇게 안내한다.
 * ⚠ 서버가 멱등이라 같은 카드를 두 번 눌러도 오류가 아니다. 그래도 눌린 카드는 즉시
 *   '담김' 으로 바꿔 둔다 — 응답을 기다리는 동안 같은 카드를 또 누르는 것이 흔하다.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import { Card } from "@astryxdesign/core/Card";
import { Section } from "@astryxdesign/core/Section";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import CardThumb from "../../components/card/CardThumb";
import { useToast } from "../../components/feedback/Toast";
import { useEdition } from "../../components/edition/EditionContext";
import EditionSelector from "../../components/edition/EditionSelector";
import { cardImageUrl, searchCards } from "../../api/card";
import { addCardToGroup, fetchCollectionGroup } from "../../api/collectionGroup";
import { COLLECTION_GROUP_KEY } from "../../hooks/useCollectionGroupQueries";
import styles from "./CollectionGroupDetailPage.module.css";

/** 입력이 멈춘 뒤에 찾는다 — 글자마다 부르면 검색 API 가 타이핑 수만큼 맞는다. */
const DEBOUNCE_MS = 300;

export default function CollectionGroupAddPage({ groupSerialNo }: { groupSerialNo: number }) {
  const { t } = useTranslation("card");
  const toast = useToast();
  const queryClient = useQueryClient();
  const { edition } = useEdition();

  const [input, setInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [added, setAdded] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<number | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setKeyword(input.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [input]);

  // 이미 담긴 카드는 검색 결과에서도 '담김' 으로 보여야 한다 — 다시 누를 이유가 없다.
  const detail = useQuery({
    queryKey: [...COLLECTION_GROUP_KEY, groupSerialNo],
    queryFn: () => fetchCollectionGroup(groupSerialNo),
    staleTime: 30_000,
  });
  const alreadyIn = useMemo(
    () => new Set((detail.data?.cards ?? []).map((c) => c.card.cardSerialNo)),
    [detail.data?.cards],
  );

  const results = useQuery({
    queryKey: ["cards", "search", keyword, edition],
    queryFn: () => searchCards({ keyword, language: edition, size: 30 }),
    enabled: keyword.length > 0,
    staleTime: 30_000,
  });

  const add = async (cardSerialNo: number, name: string) => {
    if (busy !== null) return;
    setBusy(cardSerialNo);
    try {
      await addCardToGroup(groupSerialNo, cardSerialNo);
      setAdded((prev) => new Set(prev).add(cardSerialNo));
      await queryClient.invalidateQueries({ queryKey: COLLECTION_GROUP_KEY });
      toast.success(t("collection.groupCardAdded", { name }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("collection.groupFailed"));
    } finally {
      setBusy(null);
    }
  };

  const items = results.data?.items ?? [];

  return (
    <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
      <VStack gap={4}>
        <Text type="supporting" color="secondary">{t("collection.addManualNotice")}</Text>

        {/* ⚠ 카드 에디션 칩이 **여기 있어야 한다** (2026-09-03 실기기). 검색은 앱의
            에디션으로 좁혀지는데, 묶음은 언어를 가리지 않고 담는 자리다(실제로 한국판
            에디션인 계정의 묶음에 영문 카드가 들어 있었다). 칩이 없으면 담고 싶은 카드가
            "결과 없음" 으로만 보이고 왜 안 나오는지 알 방법이 없다.
            ⚠ 이 칩은 앱 전역 에디션을 바꾼다 — 검색·세트·카드 상세와 같은 선택을
              공유하는 것이 이 블록의 계약이다(EditionSelector 주석). */}
        <EditionSelector />

        <TextInput
          label={t("collection.searchCards")}
          isLabelHidden
          value={input}
          onChange={(v) => setInput(v)}
          placeholder={t("search.placeholder")}
          hasClear
          hasAutoFocus
        />

        {keyword.length === 0 ? (
          <Text type="supporting" color="secondary">{t("collection.addManualHint")}</Text>
        ) : results.isLoading ? (
          <VStack gap={2}>
            <Skeleton width="100%" height={64} />
            <Skeleton width="100%" height={64} />
          </VStack>
        ) : items.length === 0 ? (
          <Text type="supporting" color="secondary">{t("search.empty")}</Text>
        ) : (
          <VStack gap={2}>
            {items.map((card) => {
              const inGroup = alreadyIn.has(card.cardSerialNo) || added.has(card.cardSerialNo);
              return (
                <Card key={card.cardSerialNo} padding={3}>
                  <HStack gap={3} align="center" justify="between" width="100%">
                    <HStack gap={3} align="center" className={styles.searchField}>
                      <CardThumb imgUrl={cardImageUrl(card.imagePathway) ?? undefined} />
                      <VStack gap={0.5} align="start">
                        <Text weight="semibold">{card.name}</Text>
                        <Text type="supporting" color="secondary">
                          {[card.cardsetCode?.toUpperCase(), card.number].filter(Boolean).join(" · ")}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      variant={inGroup ? "ghost" : "secondary"}
                      size="sm"
                      label={inGroup ? t("collection.groupCardIn") : t("collection.addSheet.submit")}
                      isDisabled={inGroup || busy !== null}
                      clickAction={() => void add(card.cardSerialNo, card.name)}
                    />
                  </HStack>
                </Card>
              );
            })}
          </VStack>
        )}
      </VStack>
    </Section>
  );
}
